import React from "react";
import { useLocation } from "react-router-dom";
import Helmet from "react-helmet";
// components
import DosingPatients from "../../components/DosingPatients";
import SectionTitle from "../../components/SectionTitle";
// images
import bottle from "./img/bottle.png";
import bottleMobile from "./img/bottle-mobile.png";
import vial from "../../assets/images/vial.png";
import gloves from "../../assets/images/gloves.png";
import nurse from "../../assets/images/nurse.png";
import table from "../../assets/images/prep_table_small.svg";
// styles
import shared from "./SharedStyles.module.css";
import styles from "./Dosing.module.css";

export default function Dosing() {
  let location = useLocation();
  const previewMode = location.search.includes("preview");

  return (
    <>
      <div className={styles.banner}></div>
      <div className={styles.container}>
        <Helmet>
          <meta name="title" content={process.env.REACT_APP_META_TITLE} />
          <meta
            name="description"
            content={process.env.REACT_APP_META_DESCRIPTION}
          />
          <meta name="keywords" content={process.env.REACT_APP_META_KEYWORDS} />
        </Helmet>
        <div className={shared.section}>
          <div style={{ position: "relative" }} className={styles.firstSection}>
            <div id="administration" className={styles.anchor}></div>
            <SectionTitle>
              Dosing and Administration
              <span className={styles.superScriptTitle}>a</span>
            </SectionTitle>

            <div className={styles.listSection}>
              <div className={styles.subTitle}>Starting Dosage</div>
              <ul className={styles.list}>
                <li>
                  20 ng/kg/min<span className={styles.superScriptList}>*</span>{" "}
                  via continuous intravenous infusion
                </li>
                <li>
                  Administration through a central venous line is recommended
                </li>
              </ul>
            </div>

            <div className={styles.listSection}>
              <div className={styles.subTitle}>Optimizing Treatment</div>
              <ul className={styles.list}>
                <li>
                  Monitor blood pressure response and titrate GIAPREZA as
                  frequently as every 5 minutes by increments of up to 15
                  ng/kg/min as needed to achieve or maintain blood pressure
                </li>
                <li>
                  The plasma half-life of GIAPREZA is less than one minute
                </li>
                <li>
                  Do not exceed 80 ng/kg/min during the first 3 hours of
                  treatment
                </li>
                <li>Maintenance dose should not exceed 40 ng/kg/min</li>
                <li>
                  Once the underlying shock has sufficiently improved,
                  down-titrate every 5 to 15 minutes by increments of up to 15
                  ng/kg/min based on blood pressure
                </li>
                <li>Doses as low as 1.25 ng/kg/min may be used</li>
              </ul>
              <div className={styles.tableNotes}>
                {" "}
                <span style={{ verticalAlign: "text-top" }}>*</span>20 ng/kg/min
                is equivalent to 0.02 mcg/kg/min
              </div>
              <div className={styles.footnotes}>
                <span className={styles.superScript}>a</span>{" "}
                <a
                  href="https://firebasestorage.googleapis.com/v0/b/giapreza-db.appspot.com/o/prescribing%2FGIAPREZA.pdf?alt=media"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GIAPREZA FDA prescribing information
                </a>
              </div>
            </div>
          </div>

          <div
            style={{ position: "relative" }}
            className={styles.prepContainer}
          >
            <div id="preparation" className={styles.anchor}></div>

            <SectionTitle style={{ marginBottom: 0 }}>
              Supply, Storage and Preparation
              <span className={styles.superScriptTitle}>a</span>
            </SectionTitle>

            <div className={styles.dosingSection}>
              <div>
                <div className={`${styles.listSection} ${styles.supplyList}`}>
                  <div className={styles.subTitle}>Supply</div>
                  <ul className={styles.list}>
                    <li>
                      GIAPREZA injection is supplied in a carton as a 1 mL
                      single dose vial containing 0.5 or 2.5 mg
                      angiotensin&nbsp;II (as a sterile liquid)
                    </li>
                  </ul>
                </div>
                <div className={styles.listSection}>
                  <div className={styles.subTitle}>Storage</div>
                  <ul className={styles.list}>
                    <li>
                      GIAPREZA vials should be stored in the refrigerator
                      (36-46°F, 2-8°C)
                    </li>
                    <li>
                      Diluted solution may be stored at room temperature or
                      under refrigeration for up to 24 hours, after which it
                      should be discarded
                    </li>
                  </ul>
                </div>
              </div>
              <div className={styles.bottle}>
                <img
                  src={bottleMobile}
                  alt="GIAPREZA packaging"
                  className={styles.bottlepic__mobile}
                />
                <img
                  src={bottle}
                  alt="GIAPREZA packaging"
                  className={styles.bottlepic}
                />
              </div>
            </div>
            <div className={styles.prepPicContainer}>
              <div className={styles.dosingSection}>
                <div className={styles.prep}>
                  <div className={styles.subTitle}>Preparation</div>
                  <ul className={styles.list}>
                    <li>
                      GIAPREZA should be inspected visually for particulate
                      matter and discoloration prior to administration
                    </li>
                    <li>
                      Dilute 1 mL of GIAPREZA in a normal saline (NS) (0.9%
                      sodium chloride) infusion bag to achieve a final
                      concentration as follows:
                      <div className={styles.prepTable}>
                        <img src={table} alt="Preparation Table" />
                      </div>
                    </li>
                    <li>
                      Discard vial and any unused portion of the drug product
                      after use
                    </li>
                  </ul>
                </div>
                <div className={styles.smallPics}>
                  <img src={vial} alt="Person looking a a vial" />
                  <img src={nurse} alt="A nurse changing an IV bag" />
                  <img
                    src={gloves}
                    alt="A close up of hands taking off medical gloves"
                  />
                </div>
              </div>
              <div className={styles.footnotes}>
                <span className={styles.superScript}>a</span>{" "}
                <a
                  href="https://firebasestorage.googleapis.com/v0/b/giapreza-db.appspot.com/o/prescribing%2FGIAPREZA.pdf?alt=media"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GIAPREZA FDA prescribing information
                </a>
              </div>
            </div>
          </div>
          <div className={styles.examplesContainer}>
            <div id="patients" className={styles.anchor}></div>
            <SectionTitle>EXAMPLE CANDIDATE PATIENTS</SectionTitle>

            <DosingPatients />

            {previewMode && (
              <>
                <br />
                <br />
                <br />
                <DosingPatients defaultActive={1} />
                <br />
                <br />
                <br />
                <DosingPatients defaultActive={2} />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
